import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth, signOut } from "firebase/auth";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import InstallMobileRoundedIcon from "@mui/icons-material/InstallMobileRounded";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import {
  getUserDoc,
  updateProfileInFirestore,
  fetchUserProfileFromFirestore,
  DEFAULT_NOTIFICATION_PREFERENCES,
} from "../../services/profileService";
import { usePwaPrompt } from "../../hooks/usePwaPrompt";
import { isAndroidChrome, isIOS, isPwaInstalled } from "../../utils/platform";
import { deleteMyAccount } from "../../services/accountDeletionService";
import useThemeStore, {
  selectThemeMode,
  selectSetThemeMode,
} from "../../store/useThemeStore";
import { clearSignedOutUserStorage } from "../../store/appStorage";
import usePushStatusStore, { selectPushHealth } from "../../store/usePushStatusStore";

const initialPreferences = { ...DEFAULT_NOTIFICATION_PREFERENCES };

function titleCaseKey(key) {
  return key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase());
}

function formatAccountType(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) {
    return "Personal";
  }

  return raw.replace(/(^|\s|-|_)\w/g, (match) =>
    match.replace(/[-_]/g, " ").toUpperCase()
  );
}

const THEME_OPTIONS = [
  {
    value: "light",
    label: "Light",
    description: "Always light",
  },
  {
    value: "dark",
    label: "Dark",
    description: "Default",
  },
  {
    value: "system",
    label: "System",
    description: "Match device",
  },
];

export default function AccountSettingsPage() {
  const auth = getAuth();
  const navigate = useNavigate();
  const hasFetchedRef = useRef(false);

  const [userData, setUserData] = useState(null);
  const [notificationPreferences, setNotificationPreferences] = useState(initialPreferences);
  const [hasChanged, setHasChanged] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [installStatus, setInstallStatus] = useState(null);
  const [notificationRequestStatus, setNotificationRequestStatus] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmationText, setDeleteConfirmationText] = useState("");
  const [deleteAccountBusy, setDeleteAccountBusy] = useState(false);
  const [deleteAccountError, setDeleteAccountError] = useState("");
  const { triggerPrompt, isInstallable } = usePwaPrompt();

  const themeMode = useThemeStore(selectThemeMode);
  const setThemeMode = useThemeStore(selectSetThemeMode);

  const APP_ENV_RAW = process.env.REACT_APP_ENV;
  const APP_VERSION_RAW = process.env.REACT_APP_VERSION;
  const APP_ENV = (APP_ENV_RAW ?? "").trim().toLowerCase().replace(/[^a-z0-9_-]/g, "");
  const APP_VERSION = (APP_VERSION_RAW ?? "").trim().replace(/[^0-9a-zA-Z._-]/g, "");
  const SHOW_ENV = APP_ENV.length > 0 && APP_ENV !== "production" && APP_ENV !== "prod";
  const SHOW_VERSION = APP_VERSION.length > 0;
  const pushHealth = usePushStatusStore(selectPushHealth);
  // Android used to get no button at all, so a user whose registration lapsed had
  // no way back. Offer it wherever there is actually something to fix.
  const showEnableNotificationsButton = isIOS() || pushHealth.actionable;
  const showAndroidInstallButton = isAndroidChrome() && !isPwaInstalled();

  useEffect(() => {
    getUserDoc()
      .then((docSnap) => setUserData(docSnap.data() || {}))
      .catch((err) => {
        console.error("Error fetching user:", err);
        setUserData({});
      });
  }, []);

  useEffect(() => {
    const savePreferences = async () => {
      const user = auth.currentUser;
      if (!user || !hasChanged) return;
      try {
        setSaveError("");
        await updateProfileInFirestore(user.uid, { notificationPreferences });
      } catch (err) {
        console.error("Failed to save notification preferences:", err);
        setSaveError("Failed to save notification changes.");
      }
    };
    savePreferences();
  }, [auth.currentUser, notificationPreferences, hasChanged]);

  useEffect(() => {
    const getUserProfile = async () => {
      if (hasFetchedRef.current) return;
      hasFetchedRef.current = true;

      const user = auth.currentUser;
      if (!user) return;

      const profile = await fetchUserProfileFromFirestore(user.uid);
      const backendPrefs = profile?.notificationPreferences || {};
      const orderedPrefs = Object.keys(initialPreferences).reduce((acc, key) => {
        acc[key] = backendPrefs[key] ?? true;
        return acc;
      }, {});
      setNotificationPreferences(orderedPrefs);
    };
    getUserProfile();
  }, [auth.currentUser]);

  const handlePreferenceChange = (event) => {
    const { name, checked } = event.target;
    setNotificationPreferences((prev) => ({ ...prev, [name]: checked }));
    setHasChanged(true);
  };

  const handleInstallApp = async () => {
    setInstallStatus(null);

    if (!isInstallable) {
      setInstallStatus({
        severity: "warning",
        message:
          "Chrome is not offering the install dialog right now. Open the Chrome menu (⋮), then tap Install app or Add to Home screen. If you just deleted the app, close and reopen Chrome, then come back here.",
      });
      return;
    }

    const result = await triggerPrompt();
    if (result?.prompted && result.outcome === "accepted") {
      setInstallStatus({
        severity: "success",
        message: "Chrome will finish adding Inssa to your home screen.",
      });
      return;
    }

    setInstallStatus({
      severity: "info",
      message:
        "No problem. You can try again when Chrome offers the prompt, or use the Chrome menu (⋮) → Install app.",
    });
  };

  const handleEnableNotifications = () => {
    if (typeof Notification === "undefined") {
      setNotificationRequestStatus("Notifications are not supported in this browser.");
      return;
    }

    if (Notification.permission === "denied") {
      setNotificationRequestStatus(
        "Notifications are blocked. Turn them on in iPhone Settings, then reopen INSSA."
      );
      return;
    }

    setNotificationRequestStatus("Follow the iPhone prompt to allow notifications.");
    window.dispatchEvent(new Event("inssa:request-notifications"));
  };

  const handleOpenDeleteDialog = () => {
    setDeleteAccountError("");
    setDeleteConfirmationText("");
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    if (deleteAccountBusy) return;
    setDeleteDialogOpen(false);
    setDeleteConfirmationText("");
    setDeleteAccountError("");
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmationText.trim() !== "DELETE" || deleteAccountBusy) return;
    const user = auth.currentUser;
    if (!user) {
      setDeleteAccountError("Sign in again before deleting your account.");
      return;
    }

    setDeleteAccountBusy(true);
    setDeleteAccountError("");
    try {
      await deleteMyAccount({ confirmation: "DELETE" });
      await signOut(auth).catch(() => null);
      clearSignedOutUserStorage();
      navigate("/signin", { replace: true });
    } catch (err) {
      console.error("Failed to delete account:", err);
      setDeleteAccountError(
        err?.message || "We could not delete your account. Please try again."
      );
      setDeleteAccountBusy(false);
    }
  };

  const membershipTier = useMemo(() => {
    const raw = String(userData?.subscriptionData?.tier || "free").toLowerCase();
    return raw === "insider" ? "Insider" : "Free";
  }, [userData?.subscriptionData?.tier]);
  const accountTypeLabel = useMemo(
    () => formatAccountType(userData?.accountType),
    [userData?.accountType]
  );

  const cardSx = {
    bgcolor: "background.paper",
    borderColor: "divider",
    boxShadow: (theme) => (theme.palette.mode === "dark" ? theme.shadows[3] : "none"),
  };

  const panelSx = {
    border: "1px solid",
    borderColor: "divider",
    borderRadius: 1,
    px: 1.5,
    py: 0.75,
    bgcolor: "action.hover",
  };

  if (!userData) return <Typography sx={{ p: 3 }}>Loading...</Typography>;

  return (
    <Box
      p={3}
      maxWidth="sm"
      mx="auto"
      sx={(theme) => ({
        pt: { xs: 4, sm: 3 },
        pb: `calc(env(safe-area-inset-bottom, 0px) + 16px)`,
        bgcolor: theme.palette.background.default,
      })}
    >
      <Stack spacing={2}>
        <Card variant="outlined" sx={cardSx}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 0.5 }}>
              Appearance
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Choose how the app looks. Map styling follows this mode.
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                gap: 1,
              }}
            >
              {THEME_OPTIONS.map((option) => {
                const isSelected = themeMode === option.value;

                return (
                  <Box
                    key={option.value}
                    onClick={() => setThemeMode(option.value)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        setThemeMode(option.value);
                      }
                    }}
                    aria-pressed={isSelected}
                    sx={(theme) => ({
                      ...panelSx,
                      px: 1,
                      py: 1.25,
                      cursor: "pointer",
                      textAlign: "center",
                      transition: "all 160ms ease",
                      borderColor: isSelected ? "success.main" : "divider",
                      backgroundColor: isSelected
                        ? theme.palette.mode === "dark"
                          ? "rgba(86, 195, 114, 0.14)"
                          : "rgba(86, 195, 114, 0.10)"
                        : "action.hover",
                      boxShadow: isSelected
                        ? `inset 0 0 0 1px ${theme.palette.success.main}`
                        : "none",
                    })}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, lineHeight: 1.2 }}
                    >
                      {option.label}
                    </Typography>
                    <Typography
                      variant="caption"
                      color={isSelected ? "success.main" : "text.secondary"}
                      sx={{ display: "block", mt: 0.4, lineHeight: 1.2 }}
                    >
                      {option.description}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </CardContent>
        </Card>

        {showAndroidInstallButton && (
          <Card variant="outlined" sx={cardSx}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 0.5 }}>
                App install
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Add Inssa to your Android home screen for a faster app-like experience.
              </Typography>
              <Button
                onClick={handleInstallApp}
                variant="contained"
                color="success"
                fullWidth
                startIcon={<InstallMobileRoundedIcon />}
                sx={{ py: 1.1 }}
              >
                Install Inssa
              </Button>
              {!isInstallable && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                  If Chrome does not show a dialog, use the Chrome menu (⋮) and choose Install app.
                </Typography>
              )}
              {installStatus && (
                <Alert severity={installStatus.severity} sx={{ mt: 1.5 }}>
                  {installStatus.message}
                </Alert>
              )}
            </CardContent>
          </Card>
        )}

        <Card variant="outlined" sx={cardSx}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 0.5 }}>
              Insider Status
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Current status: <strong>{membershipTier}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: membershipTier !== "Insider" ? 1.5 : 0 }}>
              Account type: <strong>{accountTypeLabel}</strong>
            </Typography>
            {membershipTier !== "Insider" && (
              <Button
                onClick={() => navigate("/membership")}
                variant="contained"
                fullWidth
                sx={(theme) => ({
                  py: 1.2,
                  ...(theme.palette.mode === "dark"
                    ? {
                        color: theme.palette.getContrastText(theme.palette.success.main),
                      }
                    : {}),
                })}
              >
                Join Insider
              </Button>
            )}
          </CardContent>
        </Card>

        <Card variant="outlined" sx={cardSx}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 0.5 }}>
              Permissions
            </Typography>
            {!pushHealth.ok && (
              <Alert severity={pushHealth.reason === "blocked" ? "warning" : "info"} sx={{ mb: 1.5 }}>
                {pushHealth.message}
              </Alert>
            )}
            {showEnableNotificationsButton && (
              <Button
                onClick={handleEnableNotifications}
                variant="contained"
                color="success"
                fullWidth
                startIcon={<NotificationsActiveRoundedIcon />}
                sx={{ mb: 1.5, py: 1.1 }}
              >
                {pushHealth.reason === "no_token" ? "Reconnect Notifications" : "Enable Notifications"}
              </Button>
            )}
            {notificationRequestStatus && (
              <Alert severity="info" sx={{ mb: 1.5 }}>
                {notificationRequestStatus}
              </Alert>
            )}
            <Typography variant="body2" color="text.secondary">
              To change permissions after denying, open your browser&apos;s site settings:
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              iOS Safari: Settings → Safari → Camera/Location/Notifications
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Android Chrome: Chrome menu (⋮) → Settings → Site settings → [Permission]
            </Typography>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={cardSx}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Notification Preferences
            </Typography>
            <Stack spacing={0.5}>
              {Object.entries(notificationPreferences ?? {}).map(([key, value]) => (
                <Box key={key} sx={{ borderRadius: 1.5, px: 0.5 }}>
                  <FormControlLabel
                    control={
                      <Checkbox color="success" checked={value} onChange={handlePreferenceChange} name={key} />
                    }
                    label={titleCaseKey(key)}
                  />
                </Box>
              ))}
            </Stack>
            {saveError && (
              <Alert severity="error" sx={{ mt: 1.5 }}>
                {saveError}
              </Alert>
            )}
          </CardContent>
        </Card>

        <Card
          variant="outlined"
          sx={{
            ...cardSx,
            borderColor: "error.main",
          }}
        >
          <CardContent>
            <Typography variant="h6" sx={{ mb: 0.5 }}>
              Danger Zone
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Permanently delete your account and remove or anonymize data connected to it.
            </Typography>
            <Button
              onClick={handleOpenDeleteDialog}
              variant="outlined"
              color="error"
              fullWidth
              startIcon={<DeleteForeverRoundedIcon />}
              sx={{ py: 1.1 }}
            >
              Delete my account
            </Button>
          </CardContent>
        </Card>
      </Stack>

      <Divider sx={{ my: 3 }} />
      <Typography
        variant="caption"
        sx={{ display: "block", textAlign: "center", opacity: 0.7 }}
      >
        {`Inssa${SHOW_ENV ? ` ${APP_ENV}` : ""}${SHOW_VERSION ? ` v${APP_VERSION}` : ""}`}
      </Typography>

      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog} fullWidth maxWidth="xs">
        <DialogTitle>Delete account</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This permanently deletes your profile, settings, history, private drafts, and self-only time capsules. Shared memories and business claim records may stay visible as Deleted user.
          </DialogContentText>
          <DialogContentText sx={{ mt: 1.5 }}>
            Type DELETE to confirm.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            margin="normal"
            label="Confirmation"
            value={deleteConfirmationText}
            onChange={(event) => setDeleteConfirmationText(event.target.value)}
            disabled={deleteAccountBusy}
            inputProps={{ "aria-label": "Type DELETE to confirm account deletion" }}
          />
          {deleteAccountError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {deleteAccountError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDeleteDialog} disabled={deleteAccountBusy}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteAccount}
            variant="contained"
            color="error"
            disabled={deleteConfirmationText.trim() !== "DELETE" || deleteAccountBusy}
          >
            {deleteAccountBusy ? "Deleting..." : "Delete account"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
